/**
 * Консоль підтримки (ч.1015, Етап 5) — робоче місце того, хто відповідає.
 *
 * Відрізняється від кабінету клієнта (`/support`) тим, що показує
 * звернення ВСІХ клієнтів і додає те, чого користувач не бачить:
 * назву клієнта, автора, позначку ескалації, внутрішні нотатки.
 *
 * Свідомо без зайвого: фільтр черги, переписка, відповідь/нотатка,
 * «взяти на себе», зміна стану. Аналітика — окремий розділ (SPEC 8.3).
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  supportConsole, type ConsoleMessage, type QueueFilter,
} from '@/api/supportConsole';
import DocStatusBadge, { type BadgeTone } from '@/shared/components/DocStatusBadge';

const STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  NEW: { label: 'нове', tone: 'blue' },
  IN_PROGRESS: { label: 'в роботі', tone: 'blue' },
  WAITING_USER: { label: 'очікує клієнта', tone: 'amber' },
  ESCALATED: { label: 'передано розробнику', tone: 'amber' },
  RESOLVED: { label: 'вирішено', tone: 'green' },
  CLOSED: { label: 'закрито', tone: 'gray' },
};

const FILTERS: { id: QueueFilter; label: string }[] = [
  { id: 'open', label: 'відкриті' },
  { id: 'escalated', label: 'передані розробнику' },
  { id: 'all', label: 'усі' },
];

function when(iso: string): string {
  return new Date(iso).toLocaleString('uk-UA',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function author(m: ConsoleMessage): string {
  if (m.author_kind === 'user') return m.author_name || 'Клієнт';
  // ч.1091: нотатка «агента» без відповіді клієнту — автоматична перевірка.
  if (m.author_kind === 'agent') return m.is_internal_note ? 'Автоматична перевірка' : 'Агент';
  return m.author_name || 'Співробітник';
}

/** ч.1091: запит на скидання двофакторного входу (публічна сторінка + лист КЕП). */
export const TWO_FACTOR_RESET = 'TWO_FACTOR_RESET';
const FINISHED = new Set(['RESOLVED', 'CLOSED']);

export default function SupportConsolePage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<QueueFilter>('open');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(false);
  const [shotOf, setShotOf] = useState<string | null>(null);
  const feedRef = useRef<HTMLDivElement>(null);

  const queue = useQuery({
    queryKey: ['sup-console-queue', filter],
    queryFn: () => supportConsole.queue(filter),
    refetchInterval: 30_000,
  });

  const active = useQuery({
    queryKey: ['sup-console-case', activeId],
    queryFn: () => supportConsole.case(activeId!),
    enabled: !!activeId,
    refetchInterval: 20_000,
  });

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight });
  }, [active.data?.messages.length]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sup-console-case', activeId] });
    qc.invalidateQueries({ queryKey: ['sup-console-queue'] });
  };

  const reply = useMutation({
    mutationFn: (status?: string) =>
      supportConsole.reply(activeId!, draft.trim(), internal, status),
    onSuccess: () => { setDraft(''); setInternal(false); invalidate(); },
  });

  const assign = useMutation({
    mutationFn: () => supportConsole.assign(activeId!, !doc?.assignee_id),
    onSuccess: invalidate,
  });

  // ч.1091: рішення — за людиною; текст у полі відповіді (якщо є) стає
  // повідомленням клієнту замість стандартного.
  const resetTfa = useMutation({
    mutationFn: () => supportConsole.twoFactorReset(activeId!, draft.trim() || null),
    onSuccess: () => { setDraft(''); setInternal(false); invalidate(); },
  });

  const doc = active.data;
  const rows = queue.data ?? [];
  const canResetTfa = !!doc && doc.category === TWO_FACTOR_RESET && !FINISHED.has(doc.status);

  return (
    <div className="card sup">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Консоль підтримки</h2>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          {FILTERS.map((f) => (
            <button key={f.id} type="button"
              className={filter === f.id ? 'primary' : undefined}
              onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="sup__body">
        <aside className="sup__list">
          {queue.isLoading && <div className="sup__muted">Завантаження…</div>}
          {!queue.isLoading && rows.length === 0 && (
            <div className="sup__muted">Звернень немає.</div>
          )}
          {rows.map((c) => (
            <button key={c.id} type="button"
              className={`sup__card${c.id === activeId ? ' active' : ''}`}
              onClick={() => setActiveId(c.id)}>
              <div className="sup__card-head">
                <span className="sup__card-title">{c.title}</span>
                <DocStatusBadge {...(STATUS[c.status]
                  ?? { label: c.status, tone: 'gray' as BadgeTone })} />
              </div>
              <div className="sup__card-meta">
                {c.contact_email ? `з сайту · ${c.contact_email} · ` : ''}
                {c.tenant_name ? `${c.tenant_name} · ` : ''}{c.public_id}
              </div>
              <div className="sup__card-meta">
                {when(c.last_message_at)}
                {c.assignee_id ? ' · взято в роботу' : ' · без виконавця'}
              </div>
            </button>
          ))}
        </aside>

        <section className="sup__feed">
          {!doc && <div className="sup__muted">Оберіть звернення зі списку.</div>}

          {doc && (
            <>
              <div className="sup__head">
                <div>
                  <div className="sup__head-id">
                    {doc.public_id}
                    {doc.tenant_name ? ` · ${doc.tenant_name}` : ''}
                    {doc.contact_email ? ` · з сайту: ${doc.contact_email}` : ''}
                    {doc.user_name ? ` · ${doc.user_name}` : ''}
                    {doc.route_pattern ? ` · сторінка: ${doc.route_pattern}` : ''}
                  </div>
                  <div className="sup__head-title">{doc.title}</div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <DocStatusBadge {...(STATUS[doc.status]
                    ?? { label: doc.status, tone: 'gray' as BadgeTone })} />
                  <button type="button" onClick={() => assign.mutate()}
                    disabled={assign.isPending}>
                    {doc.assignee_id ? 'зняти з себе' : 'взяти на себе'}
                  </button>
                  {canResetTfa && (
                    <button type="button" className="primary" disabled={resetTfa.isPending}
                      title="Секрети двофакторного входу буде стерто, усі сесії користувача — завершено; звернення закриється"
                      onClick={() => {
                        if (window.confirm('Скинути двофакторний вхід цьому користувачу? '
                          + 'Перевірте спершу лист із КЕП у внутрішній нотатці: підпис дійсний '
                          + 'і належить організації його бізнес-акаунта.')) resetTfa.mutate();
                      }}>
                      {resetTfa.isPending ? 'скидаю…' : 'скинути двофакторний вхід'}
                    </button>
                  )}
                </div>
              </div>
              {canResetTfa && (
                <div className="sup__muted" style={{ padding: '0.25rem 0 0' }}>
                  Запит на скидання двофакторного входу: клієнту обіцяно відповідь до 72 годин
                  у робочі дні. Скидайте лише за дійсним КЕП-підписом організації його
                  акаунта (див. «Автоматична перевірка»); інакше — «відповісти і закрити».
                </div>
              )}

              <div className="sup__msgs" ref={feedRef}>
                {doc.messages.map((m) => (
                  <div key={m.id}
                    className={`sup__msg${m.author_kind === 'staff' ? ' own' : ''}`
                      + (m.is_internal_note ? ' sup__msg--note' : '')}>
                    <div className="sup__msg-head">
                      <span>
                        {author(m)}
                        {m.is_internal_note && ' · внутрішня нотатка'}
                      </span>
                      <span>{when(m.created_at)}</span>
                    </div>
                    <div className="sup__msg-body">{m.body}</div>
                    {m.has_screenshot && (
                      <button type="button" className="sup__shot"
                        onClick={() => setShotOf(m.id)}>
                        {m.attachment_kind === 'kep_letter' ? 'лист із КЕП (.p7s)' : 'знімок екрана'}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="sup__reply">
                <textarea value={draft} rows={3} maxLength={8000}
                  placeholder={internal
                    ? 'Внутрішня нотатка — клієнт її не побачить'
                    : 'Відповідь клієнту…'}
                  onChange={(e) => setDraft(e.target.value)} />
                <div className="supw__opts" style={{ padding: '0.3rem 0 0' }}>
                  <label>
                    <input type="checkbox" checked={internal}
                      onChange={(e) => setInternal(e.target.checked)} />
                    внутрішня нотатка (клієнт не побачить)
                  </label>
                </div>
                <div className="sup__actions">
                  <button type="button"
                    disabled={!draft.trim() || reply.isPending}
                    onClick={() => reply.mutate('ESCALATED')}>
                    передати розробнику
                  </button>
                  <button type="button"
                    disabled={!draft.trim() || reply.isPending}
                    onClick={() => reply.mutate('RESOLVED')}>
                    відповісти і закрити
                  </button>
                  <button type="button" className="primary"
                    disabled={!draft.trim() || reply.isPending}
                    onClick={() => reply.mutate(undefined)}>
                    {reply.isPending ? 'надсилаю…' : 'надіслати'}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {shotOf && <ConsoleShot messageId={shotOf} onClose={() => setShotOf(null)} />}
    </div>
  );
}

function ConsoleShot({ messageId, onClose }:
  { messageId: string; onClose: () => void }) {
  const shot = useQuery({
    queryKey: ['sup-console-shot', messageId],
    queryFn: () => supportConsole.screenshot(messageId),
  });
  const letter = shot.data?.kind === 'kep_letter';
  return (
    <div className="sup__overlay" onClick={onClose}>
      <div className="card sup__shotbox" onClick={(e) => e.stopPropagation()}
        style={letter ? { width: 560 } : undefined}>
        <div className="sup__modal-head">
          <h3>{letter ? 'Лист із КЕП-підписом' : 'Знімок екрана клієнта'}</h3>
          <button type="button" className="sup__modal-x" aria-label="Закрити"
            onClick={onClose}>✕</button>
        </div>
        {shot.isLoading && <div className="sup__muted">Завантаження…</div>}
        {shot.isError && <div className="sup__muted">Вкладення недоступне.</div>}
        {shot.data && !letter && <img src={shot.data.content} alt="Знімок екрана"
                                      className="sup__shotimg" />}
        {shot.data && letter && (
          <div style={{ fontSize: '0.9rem' }}>
            <p style={{ margin: '0 0 0.6rem' }}>
              <a href={shot.data.content} download="lyst-2fa.p7s">завантажити лист (.p7s)</a>
            </p>
            <p className="sup__muted" style={{ padding: 0 }}>
              Якщо криптоперевірку не виконано автоматично — перевірте файл на
              czo.gov.ua/verify: підпис дійсний, а код підписанта (ЄДРПОУ / РНОКПП)
              належить організації бізнес-акаунта клієнта.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
